'use client';

import { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import type {
  TechRadarEntry,
  TechRadarQuadrant,
  TechRadarRing,
} from '@/lib/command-center/types';
import {
  TECH_RADAR_QUADRANT_LABELS,
  TECH_RADAR_RING_CONFIG,
} from '@/lib/command-center/types';

interface TechRadarProps {
  entries: TechRadarEntry[];
  onEntryClick?: (entry: TechRadarEntry) => void;
  className?: string;
  interactive?: boolean;
}

const QUADRANT_ANGLES: Record<TechRadarQuadrant, { start: number; end: number }> = {
  techniques: { start: 0, end: 90 },
  tools: { start: 90, end: 180 },
  platforms: { start: 180, end: 270 },
  languages_frameworks: { start: 270, end: 360 },
};

const RING_RADII: Record<TechRadarRing, { inner: number; outer: number }> = {
  adopt: { inner: 0, outer: 0.25 },
  trial: { inner: 0.25, outer: 0.5 },
  assess: { inner: 0.5, outer: 0.75 },
  hold: { inner: 0.75, outer: 1 },
};

const RING_COLORS: Record<TechRadarRing, string> = {
  adopt: '#22c55e',
  trial: '#3b82f6',
  assess: '#f59e0b',
  hold: '#ef4444',
};

const QUADRANT_COLORS: Record<TechRadarQuadrant, string> = {
  techniques: 'rgba(59, 130, 246, 0.1)',
  tools: 'rgba(168, 85, 247, 0.1)',
  platforms: 'rgba(249, 115, 22, 0.1)',
  languages_frameworks: 'rgba(34, 197, 94, 0.1)',
};

export function TechRadar({
  entries,
  onEntryClick,
  className,
  interactive = true,
}: TechRadarProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 600 });
  const [selectedEntry, setSelectedEntry] = useState<TechRadarEntry | null>(null);
  const [hoveredEntry, setHoveredEntry] = useState<TechRadarEntry | null>(null);

  // Handle resize
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((observedEntries) => {
      for (const entry of observedEntries) {
        const { width, height } = entry.contentRect;
        const size = Math.min(width, height, 800);
        setDimensions({ width: size, height: size });
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Calculate blip position
  const calculateBlipPosition = useCallback(
    (entry: TechRadarEntry, index: number, sameQuadrantRingEntries: TechRadarEntry[]) => {
      const quadrantAngles = QUADRANT_ANGLES[entry.quadrant];
      const ringRadii = RING_RADII[entry.ring];
      const radius = dimensions.width / 2 - 40;

      // Calculate angle with some jitter to avoid overlap
      const angleRange = quadrantAngles.end - quadrantAngles.start;
      const entryCount = sameQuadrantRingEntries.length;
      const angleStep = angleRange / (entryCount + 1);
      const baseAngle = quadrantAngles.start + angleStep * (index + 1);

      // Add small random offset for visual variety
      const angleJitter = (Math.random() - 0.5) * 10;
      const angle = ((baseAngle + angleJitter) * Math.PI) / 180;

      // Calculate radius position within the ring
      const ringWidth = ringRadii.outer - ringRadii.inner;
      const radiusOffset = ringRadii.inner + ringWidth * (0.3 + Math.random() * 0.4);
      const r = radius * radiusOffset;

      return {
        x: dimensions.width / 2 + r * Math.cos(angle - Math.PI / 2),
        y: dimensions.height / 2 + r * Math.sin(angle - Math.PI / 2),
      };
    },
    [dimensions]
  );

  // Render radar
  const renderRadar = useCallback(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { width, height } = dimensions;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2 - 40;

    // Create main group
    const g = svg.append('g');

    // Draw rings
    const rings: TechRadarRing[] = ['hold', 'assess', 'trial', 'adopt'];
    rings.forEach((ring) => {
      const ringConfig = RING_RADII[ring];
      g.append('circle')
        .attr('cx', centerX)
        .attr('cy', centerY)
        .attr('r', radius * ringConfig.outer)
        .attr('fill', 'none')
        .attr('stroke', RING_COLORS[ring])
        .attr('stroke-width', 2)
        .attr('stroke-opacity', 0.3);
    });

    // Draw quadrant lines
    const quadrantAngles = [0, 90, 180, 270];
    quadrantAngles.forEach((angle) => {
      const rad = (angle * Math.PI) / 180 - Math.PI / 2;
      g.append('line')
        .attr('x1', centerX)
        .attr('y1', centerY)
        .attr('x2', centerX + radius * Math.cos(rad))
        .attr('y2', centerY + radius * Math.sin(rad))
        .attr('stroke', '#64748b')
        .attr('stroke-width', 1)
        .attr('stroke-opacity', 0.3);
    });

    // Draw quadrant backgrounds
    const quadrants: TechRadarQuadrant[] = ['techniques', 'tools', 'platforms', 'languages_frameworks'];
    quadrants.forEach((quadrant) => {
      const angles = QUADRANT_ANGLES[quadrant];
      const startAngle = ((angles.start - 90) * Math.PI) / 180;
      const endAngle = ((angles.end - 90) * Math.PI) / 180;

      const arc = d3
        .arc<unknown>()
        .innerRadius(0)
        .outerRadius(radius)
        .startAngle(startAngle)
        .endAngle(endAngle);

      g.append('path')
        .attr('d', arc as unknown as string)
        .attr('transform', `translate(${centerX}, ${centerY})`)
        .attr('fill', QUADRANT_COLORS[quadrant]);
    });

    // Draw quadrant labels
    quadrants.forEach((quadrant) => {
      const angles = QUADRANT_ANGLES[quadrant];
      const midAngle = ((angles.start + angles.end) / 2 - 90) * (Math.PI / 180);
      const labelRadius = radius + 20;

      g.append('text')
        .attr('x', centerX + labelRadius * Math.cos(midAngle))
        .attr('y', centerY + labelRadius * Math.sin(midAngle))
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', 12)
        .attr('font-weight', 'bold')
        .attr('fill', '#94a3b8')
        .text(TECH_RADAR_QUADRANT_LABELS[quadrant]);
    });

    // Draw ring labels
    rings.forEach((ring) => {
      const ringConfig = RING_RADII[ring];
      const labelRadius = radius * ((ringConfig.inner + ringConfig.outer) / 2);

      g.append('text')
        .attr('x', centerX + labelRadius)
        .attr('y', centerY - 5)
        .attr('text-anchor', 'middle')
        .attr('font-size', 10)
        .attr('fill', RING_COLORS[ring])
        .text(TECH_RADAR_RING_CONFIG[ring].label);
    });

    // Group entries by quadrant and ring for positioning
    const groupedEntries = new Map<string, TechRadarEntry[]>();
    entries.forEach((entry) => {
      const key = `${entry.quadrant}-${entry.ring}`;
      if (!groupedEntries.has(key)) {
        groupedEntries.set(key, []);
      }
      groupedEntries.get(key)!.push(entry);
    });

    // Draw blips
    const blips = g
      .selectAll('.blip')
      .data(entries)
      .enter()
      .append('g')
      .attr('class', 'blip')
      .attr('transform', (d) => {
        const key = `${d.quadrant}-${d.ring}`;
        const sameEntries = groupedEntries.get(key) || [];
        const index = sameEntries.indexOf(d);
        const pos = calculateBlipPosition(d, index, sameEntries);
        return `translate(${pos.x}, ${pos.y})`;
      })
      .style('cursor', interactive ? 'pointer' : 'default');

    // Blip circles
    blips
      .append('circle')
      .attr('r', 8)
      .attr('fill', (d) => RING_COLORS[d.ring])
      .attr('stroke', '#0A1E2E')
      .attr('stroke-width', 2)
      .attr('opacity', 0.9);

    // Movement indicators
    blips
      .filter((d) => d.movement === 'up' || d.movement === 'down')
      .append('text')
      .attr('dy', 1)
      .attr('text-anchor', 'middle')
      .attr('font-size', 10)
      .attr('font-weight', 'bold')
      .attr('fill', '#0A1E2E')
      .text((d) => (d.movement === 'up' ? '↑' : '↓'));

    // New entry indicator
    blips
      .filter((d) => d.movement === 'new')
      .append('text')
      .attr('dy', 1)
      .attr('text-anchor', 'middle')
      .attr('font-size', 8)
      .attr('font-weight', 'bold')
      .attr('fill', '#0A1E2E')
      .text('★');

    // Hover interactions
    if (interactive) {
      blips
        .on('mouseenter', function (event, d) {
          d3.select(this).select('circle').transition().duration(150).attr('r', 12);
          setHoveredEntry(d);
        })
        .on('mouseleave', function () {
          d3.select(this).select('circle').transition().duration(150).attr('r', 8);
          setHoveredEntry(null);
        })
        .on('click', (event, d) => {
          event.stopPropagation();
          setSelectedEntry(d);
          onEntryClick?.(d);
        });
    }

    // Click on background to deselect
    svg.on('click', () => {
      setSelectedEntry(null);
    });
  }, [entries, dimensions, interactive, calculateBlipPosition, onEntryClick]);

  useEffect(() => {
    renderRadar();
  }, [renderRadar]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        className="w-full h-full"
      />

      {/* Hover tooltip */}
      {hoveredEntry && (
        <div className="absolute pointer-events-none bg-background/95 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-border max-w-xs z-10"
          style={{
            left: '50%',
            bottom: '20px',
            transform: 'translateX(-50%)',
          }}
        >
          <div className="flex items-center gap-2 mb-1">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: RING_COLORS[hoveredEntry.ring] }}
            />
            <span className="font-medium text-foreground">{hoveredEntry.name}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-foreground-tertiary">
            <span>{TECH_RADAR_QUADRANT_LABELS[hoveredEntry.quadrant]}</span>
            <span>•</span>
            <span className={TECH_RADAR_RING_CONFIG[hoveredEntry.ring].color}>
              {TECH_RADAR_RING_CONFIG[hoveredEntry.ring].label}
            </span>
            {hoveredEntry.movement !== 'none' && hoveredEntry.movement !== 'new' && (
              <>
                <span>•</span>
                <span>{hoveredEntry.movement === 'up' ? '↑ Moving up' : '↓ Moving down'}</span>
              </>
            )}
            {hoveredEntry.movement === 'new' && (
              <>
                <span>•</span>
                <span>★ New</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Selected entry panel */}
      {selectedEntry && (
        <div className="absolute top-4 right-4 bg-background/95 backdrop-blur-sm rounded-lg p-4 shadow-lg border border-border max-w-xs z-10">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: RING_COLORS[selectedEntry.ring] }}
              />
              <span className="font-medium text-foreground">{selectedEntry.name}</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedEntry(null);
              }}
              className="text-foreground-tertiary hover:text-foreground"
            >
              ✕
            </button>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-foreground-tertiary">
              <span>{TECH_RADAR_QUADRANT_LABELS[selectedEntry.quadrant]}</span>
              <span>•</span>
              <span className={TECH_RADAR_RING_CONFIG[selectedEntry.ring].color}>
                {TECH_RADAR_RING_CONFIG[selectedEntry.ring].label}
              </span>
            </div>
            {selectedEntry.description && (
              <p className="text-foreground-secondary">{selectedEntry.description}</p>
            )}
            {selectedEntry.rationale && (
              <div className="p-2 bg-background/50 rounded text-foreground-tertiary text-xs">
                {selectedEntry.rationale}
              </div>
            )}
            {selectedEntry.movement_reason && (
              <div className="text-xs text-foreground-tertiary">
                Movement reason: {selectedEntry.movement_reason}
              </div>
            )}
            {selectedEntry.tags && selectedEntry.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {selectedEntry.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-1.5 py-0.5 text-xs bg-cream/10 text-foreground-tertiary rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm rounded-lg p-3 border border-border-muted">
        <div className="text-xs text-foreground-tertiary mb-2">Rings</div>
        <div className="space-y-1">
          {(['adopt', 'trial', 'assess', 'hold'] as TechRadarRing[]).map((ring) => (
            <div key={ring} className="flex items-center gap-2 text-xs">
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: RING_COLORS[ring] }}
              />
              <span className="text-foreground-secondary">{TECH_RADAR_RING_CONFIG[ring].label}</span>
            </div>
          ))}
        </div>
        <div className="text-xs text-foreground-tertiary mt-3 mb-2">Movement</div>
        <div className="space-y-1 text-xs text-foreground-secondary">
          <div>↑ Moving up</div>
          <div>↓ Moving down</div>
          <div>★ New entry</div>
        </div>
      </div>
    </div>
  );
}

export default TechRadar;
